export type SubscriptionProvider = "codex" | "anthropic";

export type CodexSubscriptionModelId =
	| "openai/gpt-5.6-sol"
	| "openai/gpt-5.6-terra"
	| "openai/gpt-5.6-luna";

export type AnthropicSubscriptionModelId =
	| "anthropic/claude-opus-5"
	| "anthropic/claude-sonnet-5"
	| "anthropic/claude-haiku-4.5";

export type SubscriptionModelId =
	| CodexSubscriptionModelId
	| AnthropicSubscriptionModelId;

type SubscriptionModelBase = {
	providerModelId: string;
	modelFamily: string;
	contextWindowTokens: number;
};

export type SubscriptionModel =
	| (SubscriptionModelBase & {
			id: CodexSubscriptionModelId;
			provider: "codex";
			compactAtTokens: number;
			targetPromptTokens: number;
	  })
	| (SubscriptionModelBase & {
			id: AnthropicSubscriptionModelId;
			provider: "anthropic";
			compactAtTokens?: number;
			targetPromptTokens?: number;
	  });

export const SUBSCRIPTION_MODEL_IDS = [
	"openai/gpt-5.6-sol",
	"openai/gpt-5.6-terra",
	"openai/gpt-5.6-luna",
	"anthropic/claude-opus-5",
	"anthropic/claude-sonnet-5",
	"anthropic/claude-haiku-4.5",
] as const satisfies readonly SubscriptionModelId[];

const SUBSCRIPTION_MODELS_BY_ID: Record<
	SubscriptionModelId,
	SubscriptionModel
> = {
	"openai/gpt-5.6-sol": {
		id: "openai/gpt-5.6-sol",
		providerModelId: "gpt-5.6-sol",
		provider: "codex",
		modelFamily: "gpt-5.6",
		contextWindowTokens: 1_000_000,
		compactAtTokens: 900_000,
		targetPromptTokens: 600_000,
	},
	"openai/gpt-5.6-terra": {
		id: "openai/gpt-5.6-terra",
		providerModelId: "gpt-5.6-terra",
		provider: "codex",
		modelFamily: "gpt-5.6",
		contextWindowTokens: 1_000_000,
		compactAtTokens: 900_000,
		targetPromptTokens: 600_000,
	},
	"openai/gpt-5.6-luna": {
		id: "openai/gpt-5.6-luna",
		providerModelId: "gpt-5.6-luna",
		provider: "codex",
		modelFamily: "gpt-5.6",
		contextWindowTokens: 1_000_000,
		compactAtTokens: 900_000,
		targetPromptTokens: 600_000,
	},
	"anthropic/claude-opus-5": {
		id: "anthropic/claude-opus-5",
		provider: "anthropic",
		providerModelId: "claude-opus-5",
		modelFamily: "opus",
		contextWindowTokens: 1_000_000,
	},
	"anthropic/claude-sonnet-5": {
		id: "anthropic/claude-sonnet-5",
		provider: "anthropic",
		providerModelId: "claude-sonnet-5",
		modelFamily: "sonnet",
		contextWindowTokens: 1_000_000,
	},
	"anthropic/claude-haiku-4.5": {
		id: "anthropic/claude-haiku-4.5",
		provider: "anthropic",
		providerModelId: "claude-haiku-4-5",
		modelFamily: "haiku",
		contextWindowTokens: 200_000,
	},
};

export const SUBSCRIPTION_MODELS = SUBSCRIPTION_MODEL_IDS.map(
	(id) => SUBSCRIPTION_MODELS_BY_ID[id],
);

export function isSubscriptionModelId(id: unknown): id is SubscriptionModelId {
	return typeof id === "string" && Object.hasOwn(SUBSCRIPTION_MODELS_BY_ID, id);
}

export function isCodexSubscriptionModelId(
	id: unknown,
): id is CodexSubscriptionModelId {
	return (
		isSubscriptionModelId(id) && getSubscriptionModel(id).provider === "codex"
	);
}

export function getSubscriptionModel(
	id: SubscriptionModelId,
): SubscriptionModel {
	return SUBSCRIPTION_MODELS_BY_ID[id];
}
